import React from 'react';
import { motion } from 'framer-motion';

const PricingSection = () => {
  const plans = [
    {
      name: "Forever Free",
      price: "$0",
      period: "forever",
      description: "Perfect for getting started with synthetic data generation",
      features: {
        cases: "1",
        datasetsPerCase: "2",
        scenariosPerCase: "5",
        deployed: "Cloud",
        linesInDataset: "up to 50,000"
      },
      cta: "Get Started Free",
      ctaLink: "https://calendly.com/aol-vossmoos/realistic-synthetic-datasets",
      popular: false,
      gradient: "from-gray-500 to-gray-600"
    },
    {
      name: "Premium",
      price: "$49",
      period: "per month",
      description: "Ideal for growing teams and production workloads",
      features: {
        cases: "up to 5",
        datasetsPerCase: "5",
        scenariosPerCase: "up to 20",
        deployed: "Cloud",
        apiCli: "available",
        linesInDataset: "up to 10,000,000"
      },
      cta: "Start Premium",
      ctaLink: "https://calendly.com/aol-vossmoos/realistic-synthetic-datasets",
      popular: true,
      gradient: "from-gray-500 to-gray-600"
    },
    {
      name: "Enterprise",
      price: "Contact Sales",
      period: "",
      description: "Custom solutions for large-scale operations",
      features: {
        cases: "unlimited",
        datasetsPerCase: "unlimited",
        scenariosPerCase: "unlimited",
        deployed: "Cloud/Onprem",
        apiCli: "available",
        linesInDataset: "unlimited"
      },
      cta: "Contact Sales",
      ctaLink: "https://calendly.com/aol-vossmoos/realistic-synthetic-datasets",
      popular: false,
      gradient: "from-gray-500 to-gray-600"
    }
  ];

  const featureLabels = {
    cases: "Cases",
    datasetsPerCase: "Datasets per case",
    scenariosPerCase: "Scenarios per case",
    deployed: "Deployed",
    apiCli: "API & CLI",
    linesInDataset: "Lines in a dataset"
  };

  return (
    <section id="pricing" className="section relative overflow-hidden bg-background">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-accent/5 to-primary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Simple Pricing
          </h2>
          <p className="text-lg text-foreground-muted max-w-3xl mx-auto leading-relaxed">
            Choose the plan that fits your synthetic data generation needs
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0, 0.2, 1] }}
              className="relative"
            >
              <div className="card-elevated h-full flex flex-col group relative overflow-hidden">
                {/* Header */}
                <div className={`bg-gradient-to-r ${plan.gradient} py-6 px-6 mb-6`}>
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="text-white/90">
                    <span className="text-3xl font-bold">{plan.price}</span>
                    {plan.period && <span className="text-lg ml-1">{plan.period}</span>}
                  </div>
                </div>

                <div className="px-6 pb-6 flex flex-col h-full">
                  <p className="text-foreground-muted mb-6 text-center">
                    {plan.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-4 mb-8 flex-grow">
                    {Object.entries(plan.features).map(([key, value]) => (
                      <div key={key} className={`flex justify-between items-center ${key === 'linesInDataset' ? 'bg-accent/10 p-3 rounded-lg border border-accent/20' : ''}`}>
                        <span className={`text-sm ${key === 'linesInDataset' ? 'text-foreground font-semibold' : 'text-foreground-muted'}`}>
                          {featureLabels[key]}:
                        </span>
                        <span className={`font-medium text-sm ${key === 'linesInDataset' ? 'text-accent font-bold text-lg' : 'text-foreground'}`}>
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="text-center mt-16"
        >
          <p className="text-sm text-foreground-secondary max-w-3xl mx-auto leading-relaxed font-mono">
            <span className="text-foreground-secondary mr-3">[INFO]</span>
            <span className="ml-3">All plans include access to our AI-powered synthetic data generation platform with consistent labeling and metadata</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
